import React, { FC, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { rgba } from 'color2k';
import { useRouter } from 'next/dist/client/router';
import { Container } from '$components/layouts';
import { ProductMedia } from '$templates/product-page/product-media';
import { ICMSPage, IEnrichedProduct, IProductPage, IProductShipping, IReview, ISimpleProduct } from '$models';
import { mq } from '$lib/helpers';
import { Loading } from '$components/elements/loading/loading';
import { SSPResult } from '$lib/serversideprops-with-frame';
import { Spots } from '$components/spots/spots';
import { useBasket, useSite } from '~/store';
import { useGtm, usePageData, useRaptor } from '$hooks';
import { PageTypes } from '$constants';
import { DataTable } from '$components/elements/data-table';
import { ProductHeader } from './product-header';
import { ProductMediaDescription } from './product-media-description';
import { ProductInfoSidePanel, ProductInfoSidePanelRef } from './product-info-side-panel/product-info-side-panel';
import { ProductVariantSelector } from './product-variant-selector';
import { ProductWebRef } from './product-web-ref';
import { ProductAboutLinks } from './product-about-links';
import { FlixMedia } from './flix-media';
import { ProductAddReview } from './productAddReview';
import { ProductCtaSection } from './product-cta-section';
import { ProductPurchase } from './product-purchase';
import { ProductUsps } from './product-usps';
import { useProductSameDayDelivery } from './hooks';

export type IProductPageProps = {
    selectedProduct: IEnrichedProduct;
    reviews: IReview;
    selectedItemNumber: string;
    shippingInfo: IProductShipping;
    debug: boolean;
    variants: ISimpleProduct[];
    sameDayDeliveryEnabled: boolean;
} & ICMSPage;

export type ModalRef = {
    open(): void;
    close(): void;
};

export const ProductPage: FC<SSPResult<IProductPageProps>> = ({
    selectedProduct,
    variants,
    reviews,
    content,
    bottomContent,
    shippingInfo,
    debug,
    sameDayDeliveryEnabled,
}: SSPResult<IProductPageProps>): JSX.Element => {
    const { query, isFallback, isReady, replace, pathname } = useRouter();
    const { openReview } = query;
    const { basketId } = useBasket();
    const { trackVisitEvent } = useRaptor();
    const raptorId = useSite((state) => state.raptorId);
    const { trackProductDetailImpression, trackProductAmount } = useGtm();

    const {
        sameDayDeliveryData,
        selectedZipCode,
        isLoading: validatingSameDayDelivery,
        onZipCodeChange,
    } = useProductSameDayDelivery(selectedProduct?.product?.itemNumber);

    const {
        pageData: pageContent,
        topSpots,
        bottomSpots,
    } = usePageData<IProductPage>(content, bottomContent, PageTypes.ProductPage);

    const reviewRef = useRef<ModalRef>(null);
    const infoPanelRef = useRef<ProductInfoSidePanelRef>(null);

    useEffect(() => {
        if (isReady && openReview) {
            setTimeout(() => {
                reviewRef.current?.open?.();
                delete query.openReview;
                replace({ pathname, query }, undefined, { shallow: true });
            }, 200);
        }
    }, [isReady]);

    useEffect(() => {
        if (selectedProduct) {
            trackProductDetailImpression({
                name: selectedProduct?.product?.authors?.length
                    ? selectedProduct.product.authors.join(',') + ' : ' + selectedProduct?.name
                    : selectedProduct?.name,
                id: selectedProduct?.product?.itemNumber,
                price: selectedProduct?.activePrice?.amount,
                brand:
                    selectedProduct?.product?.itemPublisher ||
                    selectedProduct?.product?.itemSupplier?.join(',') ||
                    undefined,
                category: selectedProduct?.product?.itemGroupName,
                variant: selectedProduct?.product?.itemBinding || selectedProduct?.product?.itemColorMain,
            });

            if (selectedProduct?.product?.itemNumber && selectedProduct?.activePrice) {
                trackProductAmount(selectedProduct.product.itemNumber, selectedProduct.activePrice?.amount || 0);
            }
        }
        if (selectedProduct && !!raptorId) {
            trackVisitEvent(selectedProduct, basketId);
        }
    }, [selectedProduct, raptorId]);

    if (isFallback) {
        return <Loading />;
    }

    return (
        <Container disableMobilePadding>
            {!!topSpots.length && <Spots elements={topSpots} />}
            <GridContainer>
                <ProductHeaderContainer>
                    <div>
                        <ProductHeader
                            variant={selectedProduct}
                            onReviewClick={() => infoPanelRef.current?.open?.('reviews')}
                        />
                    </div>
                    <ProductCtas>
                        <ProductCtaSection />
                    </ProductCtas>
                </ProductHeaderContainer>
                <ProductMediaContainer>
                    <ProductMedia
                        images={selectedProduct.imageResources}
                        itemGiftShop={selectedProduct?.product?.itemGiftShop}
                        b2CDeliveryMethod={selectedProduct.b2CDeliveryMethod}
                        splashes={
                            selectedProduct?.product?.itemPriceTagUrl ? [selectedProduct.product.itemPriceTagUrl] : []
                        }
                        linkSplashUrl={selectedProduct?.product?.itemPriceTagLinkUrl}
                        isOffer={
                            !!selectedProduct.activePrice?.amount &&
                            !!selectedProduct.inActivePrice?.amount &&
                            selectedProduct.activePrice.amount < selectedProduct.inActivePrice.amount
                        }
                    />
                </ProductMediaContainer>
                <ProductShortDescriptionContainer>
                    <ProductMediaDescription
                        series={selectedProduct.series}
                        shortDescription={selectedProduct.product?.shortDescription}
                        onReadMore={() => infoPanelRef.current?.open?.('description')}
                        description={selectedProduct.product?.description}
                    />
                </ProductShortDescriptionContainer>
                <ProductDetailsOuterContainer>
                    <ProductDetailsContainer>
                        <ProductVariantSelector
                            selectedVariant={selectedProduct}
                            variants={variants}
                            formatInformation={pageContent?.formatInformation}
                        />
                        <DetailsContent>
                            <ProductPurchase
                                selectedVariant={selectedProduct}
                                findStoreUrl={pageContent?.findStoreUrl}
                                selectedZipCode={selectedZipCode}
                            />
                            <ProductUsps
                                variant={selectedProduct}
                                deliveryText={shippingInfo?.defaultShippingDeliveryTimeText}
                                shopInformation={shippingInfo?.shopInformation}
                                pageContent={pageContent}
                                selectedZipCode={selectedZipCode}
                                onZipCodeChange={onZipCodeChange}
                                sameDayDeliveryData={sameDayDeliveryData}
                                sameDayDeliveryEnabled={sameDayDeliveryEnabled}
                                validatingSameDayDelivery={validatingSameDayDelivery}
                            />
                        </DetailsContent>
                    </ProductDetailsContainer>
                </ProductDetailsOuterContainer>
            </GridContainer>
            <BottomContent>
                <FlixMedia selectedProduct={selectedProduct} />

                {debug && <DataTable data={selectedProduct} />}
                <ProductAboutLinks
                    isBook={selectedProduct.product?.isBook}
                    product={selectedProduct}
                    reviews={reviews}
                    persons={selectedProduct.persons}
                    onOpenReviewModal={() => reviewRef.current?.open?.()}
                />
                {!!selectedProduct.product?.webRefs?.length && (
                    <ProductWebRef webRefs={selectedProduct.product?.webRefs} productTitle={selectedProduct.name} />
                )}

                {!!bottomSpots.length && (
                    <>
                        <Divider />
                        <Spots elements={bottomSpots} />
                    </>
                )}
                <ProductAddReview product={selectedProduct.product} ref={reviewRef} />
                <ProductInfoSidePanel
                    product={selectedProduct}
                    reviews={reviews}
                    onOpenReviewModal={() => reviewRef.current?.open?.()}
                    themas={selectedProduct.themaCodeBreadCrumbs}
                    ref={infoPanelRef}
                />
            </BottomContent>
        </Container>
    );
};

const GridContainer = styled.div(({ theme }) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateAreas: '"header" "image" "shortDescription" "details"',
    gridTemplateColumns: '100%',
    gridTemplateRows: 'auto auto auto',
    marginBottom: theme.space[11],

    [mq('frame')]: {
        gridTemplateAreas: '"image header" "image shortDescription" "image details"',
        gridTemplateColumns: '55% auto',
        gridTemplateRows: 'auto 1fr',
        '&:before': {
            content: "''",
            display: 'block',
            position: 'absolute',
            width: '100vw',
            maxWidth: 'min(88vw, 1600px)',
            height: '100%',
            top: 0,
            left: 0,
            backgroundColor: theme.colors.grey5,
            zIndex: -1,
            borderRadius: '24px',
        },
    },
}));

const DetailsContent = styled.div({});

const ProductHeaderContainer = styled.div(({ theme }) => ({
    gridArea: 'header',
    paddingBottom: theme.space[4],
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    [mq(0, 'frame')]: {
        paddingLeft: theme.sizes.siteGutter,
        paddingRight: theme.sizes.siteGutter,
    },
    [mq('frame')]: {
        paddingTop: theme.space[11],
        paddingRight: theme.space[6],
        paddingBottom: theme.space[8],
    },
}));

const ProductMediaContainer = styled.div(({ theme }) => ({
    gridArea: 'image',
    position: 'relative',
    [mq(0, 'frame')]: {
        backgroundColor: theme.colors.grey5,
        paddingLeft: theme.sizes.siteGutter,
        paddingRight: theme.sizes.siteGutter,
    },
}));

const ProductShortDescriptionContainer = styled.div(({ theme }) => ({
    gridArea: 'shortDescription',
    paddingBottom: theme.space[11],
    position: 'relative',
    [mq(0, 'frame')]: {
        backgroundColor: theme.colors.grey5,
        paddingLeft: theme.sizes.siteGutter,
        paddingRight: theme.sizes.siteGutter,
    },
    [mq('frame')]: {
        paddingBottom: theme.space[10],
        paddingRight: `calc(${theme.space[9]} + 48px)`,
        '&:before': {
            maxWidth: theme.sizes.contentMaxWidth,
        },
    },
}));

const ProductDetailsOuterContainer = styled.div(({ theme }) => ({
    gridArea: 'details',
    position: 'relative',
    [mq(0, 'frame')]: {
        paddingLeft: theme.sizes.siteGutter,
        paddingRight: theme.sizes.siteGutter,

        '&:before': {
            content: "''",
            display: 'block',
            position: 'absolute',
            width: '100%',
            height: '30px',
            top: '-30px',
            marginLeft: `calc(${theme.sizes.siteGutter} * -1)`,
            backgroundColor: 'white',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
        },
    },
    [mq('frame')]: {
        padding: theme.space[6],
        marginBottom: theme.space[9],
        marginRight: `calc(48px + ${theme.space[9]})`,
        borderRadius: '24px',
        backgroundColor: rgba(255, 255, 255, 0.9),
    },
}));

const ProductDetailsContainer = styled.div(({ theme }) => ({
    [mq('frame')]: {
        backgroundColor: theme.colors.white,
    },
}));

const ProductCtas = styled.div(({ theme }) => ({
    display: 'block',
    [mq(0, 'frame')]: {
        alignSelf: 'flex-end',
        marginRight: theme.space[3],
    },

    [mq('frame')]: {
        display: 'flex',
        alignSelf: 'flex-start',
        paddingLeft: theme.space[8],
    },
}));

const BottomContent = styled.div(({ theme }) => ({
    [mq(0, 'frame')]: {
        paddingLeft: theme.sizes.siteGutter,
        paddingRight: theme.sizes.siteGutter,
    },
}));

const Divider = styled.div(({ theme }) => ({
    display: 'none',
    [mq('frame')]: {
        display: 'block',
        borderBottom: theme.general.border,
        width: '100%',
    },
}));
